import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import type { FixedAsset } from "@/gen/omnios/v1/accounting_pb";
import {
	useCreateFixedAsset,
	useDisposeFixedAsset,
} from "@/hooks/use-accounting";
import { formatDate } from "@/lib/format";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { useGridLayout } from "@/lib/use-grid-layout";
import { useServerGrid } from "@/lib/use-server-grid";
import { accountingClient } from "@/lib/accounting-client";

import type {
	ColDef,
	ValueFormatterParams,
} from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { createFileRoute } from "@tanstack/react-router";
import { ChevronDown, Loader2, Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGridSelection } from "@/hooks/use-grid-selection";

export const Route = createFileRoute("/accounting/assets")({
	component: FixedAssetsPage,
});

const STATUS_COLORS: Record<string, string> = {
	draft: "hsl(var(--muted-foreground))",
	in_service: "hsl(var(--chart-2))",
	fully_depreciated: "hsl(var(--muted-foreground))",
	impaired: "hsl(var(--destructive))",
	disposed: "hsl(var(--destructive))",
};

const STATUS_I18N: Record<string, string> = {
	draft: "status_draft",
	in_service: "asset_status_in_service",
	fully_depreciated: "asset_status_fully_depreciated",
	impaired: "asset_status_impaired",
	disposed: "asset_status_disposed",
};

const METHOD_I18N: Record<string, string> = {
	straight_line: "depreciation_straight_line",
	declining_balance: "depreciation_declining_balance",
	units_of_production: "depreciation_units_of_production",
};

function formatAmount(value: number): string {
	return new Intl.NumberFormat("ru-RU", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(value);
}

function FixedAssetsPage() {
	const { t } = useTranslation("accounting");
	useBreadcrumbs([{ label: t("nav_assets") }]);
	const { onGridReady, onStateChanged } = useGridLayout("assets");
	const { selectedRows, hasSelection, onSelectionChanged, ActionBar } =
		useGridSelection<FixedAsset>();
	const [statusFilter, setStatusFilter] = useState<string>("all");
	const [createOpen, setCreateOpen] = useState(false);
	const [disposeDialogOpen, setDisposeDialogOpen] = useState(false);
	const [disposeReason, setDisposeReason] = useState("");

	const createAsset = useCreateFixedAsset();
	const disposeAsset = useDisposeFixedAsset();

	const extraFilter = useMemo(() => {
		const f: Record<string, unknown> = {};
		if (statusFilter !== "all") f.status = statusFilter;
		return f;
	}, [statusFilter]);

	const { gridRef, gridProps, totalRows, refreshGrid } = useServerGrid<FixedAsset>({
		colToField: {
			acquisitionCost: "acquisition_cost",
			acquisitionDate: "acquisition_date",
			carryingValue: "carrying_value",
			accumulatedDepreciation: "accumulated_depreciation",
		},
		fetchRows: async ({ pageSize, pageToken, sort, filter, search: searchStr }) => {
			const resp = await accountingClient.listFixedAssets({
				page: { pageSize, pageToken },
				sort: sort ? { field: sort.field, descending: sort.descending } : undefined,
				filter: filter as never,
				status: statusFilter !== "all" ? statusFilter : undefined,
				search: searchStr ?? "",
			});
			return {
				rows: resp.assets,
				nextPageToken: resp.page?.nextPageToken,
				totalCount: resp.page?.totalCount ?? 0,
			};
		},
		extraFilter,
	});

	const columnDefs = useMemo<ColDef<FixedAsset>[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 40,
				maxWidth: 40,
				pinned: "left" as const,
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				field: "assetNumber",
				headerName: t("asset_number"),
				width: 120,
				pinned: "left" as const,
				cellClass: "font-mono",
			},
			{
				field: "name",
				headerName: t("account_name"),
				flex: 1,
				minWidth: 200,
			},
			{
				field: "acquisitionCost",
				headerName: t("acquisition_cost"),
				width: 140,
				type: "rightAligned",
				filter: "agNumberColumnFilter",
				cellClass: "font-tabular",
				valueGetter: (p) => Number.parseFloat(p.data?.acquisitionCost || "0"),
				valueFormatter: (p: ValueFormatterParams) => formatAmount(p.value ?? 0),
			},
			{
				field: "acquisitionDate",
				headerName: t("acquisition_date"),
				width: 110,
				filter: "agDateColumnFilter",
				valueFormatter: (p: ValueFormatterParams<FixedAsset>) => {
					const d = p.data?.acquisitionDate;
					return d ? formatDate(d.toDate(), "dd.MM.yyyy") : "\u2014";
				},
			},
			{
				field: "status",
				headerName: t("entry_status"),
				width: 130,
				filter: false,
				cellRenderer: (p: { value: string }) => {
					const color = STATUS_COLORS[p.value] ?? "hsl(var(--muted-foreground))";
					const label = t(STATUS_I18N[p.value] ?? p.value);
					return <span style={{ color }}>{label}</span>;
				},
			},
			{
				field: "carryingValue",
				headerName: t("carrying_value"),
				width: 140,
				type: "rightAligned",
				filter: "agNumberColumnFilter",
				cellClass: "font-tabular",
				valueGetter: (p) => Number.parseFloat(p.data?.carryingValue || "0"),
				valueFormatter: (p: ValueFormatterParams) => formatAmount(p.value ?? 0),
			},
			{
				field: "depreciationMethod",
				headerName: t("depreciation_method"),
				width: 160,
				valueFormatter: (p: ValueFormatterParams<FixedAsset>) =>
					t(METHOD_I18N[p.value] ?? p.value),
			},
			{
				field: "accumulatedDepreciation",
				headerName: t("accumulated_depreciation"),
				headerTooltip: t("accumulated_depreciation"),
				width: 140,
				type: "rightAligned",
				filter: "agNumberColumnFilter",
				cellClass: "font-tabular",
				valueGetter: (p) =>
					Number.parseFloat(p.data?.accumulatedDepreciation || "0"),
				valueFormatter: (p: ValueFormatterParams) => formatAmount(p.value ?? 0),
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			filter: true,
			filterParams: {
				buttons: ["apply", "reset"],
				closeOnApply: true,
			},
		}),
		[],
	);

	const canDispose = selectedRows.some((r) => r.status !== "disposed");

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "selection",
				content: hasSelection
					? t("rows_selected", { count: selectedRows.length })
					: t("row_count", { count: totalRows }),
				align: "left" as const,
			},
		],
		[hasSelection, selectedRows.length, totalRows, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						onChange={(e) =>
							gridRef.current?.api?.setGridOption(
								"quickFilterText",
								e.target.value,
							)
						}
						className="pl-9 h-9"
					/>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => setDisposeDialogOpen(true)}
					disabled={!canDispose}
				>
					{t("asset_dispose")}
				</Button>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="outline" size="sm" className="h-9">
							{t("filter_by_status")}
							<ChevronDown className="h-3.5 w-3.5 ml-1 opacity-50" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem onClick={() => setStatusFilter("all")}>
							{t("filter_all")}
						</DropdownMenuItem>
						<DropdownMenuItem onClick={() => setStatusFilter("draft")}>
							{t("status_draft")}
						</DropdownMenuItem>
						<DropdownMenuItem onClick={() => setStatusFilter("in_service")}>
							{t("asset_status_in_service")}
						</DropdownMenuItem>
						<DropdownMenuItem
							onClick={() => setStatusFilter("fully_depreciated")}
						>
							{t("asset_status_fully_depreciated")}
						</DropdownMenuItem>
						<DropdownMenuItem onClick={() => setStatusFilter("disposed")}>
							{t("asset_status_disposed")}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="outline" size="sm" className="h-9">
							{t("toolbar_export")}
							<ChevronDown className="h-3.5 w-3.5 ml-1 opacity-50" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem
							onClick={() => gridRef.current?.api?.exportDataAsCsv()}
						>
							CSV
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
				<Button size="sm" className="h-9" onClick={() => setCreateOpen(true)}>
					<Plus className="h-4 w-4 mr-1" />
					{t("asset_create")}
				</Button>
			</div>
			<div className="flex-1 ag-theme-quartz" style={{ width: "100%" }}>
				<AgGridReact<FixedAsset>
					ref={gridRef}
					{...gridProps}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					rowSelection="multiple"
					onSelectionChanged={onSelectionChanged}
					onGridReady={onGridReady}
					onSortChanged={() => {
						onStateChanged();
						refreshGrid();
					}}
					onFilterChanged={() => { onStateChanged(); refreshGrid(); }}
					onColumnMoved={onStateChanged}
					onColumnResized={onStateChanged}
					onColumnVisible={onStateChanged}
					suppressRowClickSelection
					rowHeight={28}
					headerHeight={32}
					animateRows={false}
					getRowId={(p) => p.data.id}
					overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("empty_assets_title", { defaultValue: "Нет основных средств" })}</p><p style="font-size:11px;color:hsl(var(--muted-foreground));margin-top:4px">${t("empty_assets_desc", { defaultValue: "Зарегистрируйте первый актив" })}</p></div>`}
				/>
			</div>

			<ActionBar />
			{/* Create Asset Dialog */}
			<Dialog open={createOpen} onOpenChange={setCreateOpen}>
				<CreateAssetDialog
					onSubmit={(values) => {
						createAsset.mutate(values, {
							onSuccess: () => {
								setCreateOpen(false);
								refreshGrid();
							},
						});
					}}
					isPending={createAsset.isPending}
				/>
			</Dialog>

			{/* Dispose Confirmation */}
			<AlertDialog
				open={disposeDialogOpen}
				onOpenChange={(open) => {
					setDisposeDialogOpen(open);
					if (!open) setDisposeReason("");
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("asset_dispose")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("asset_dispose_confirm", {
								name: selectedRows.map((r) => r.name).join(", "),
							})}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<div className="py-2">
						<Input
							value={disposeReason}
							onChange={(e) => setDisposeReason(e.target.value)}
							placeholder={t("dispose_reason_placeholder", {
								defaultValue: "Причина списания",
							})}
							className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
						/>
					</div>
					<AlertDialogFooter>
						<AlertDialogCancel>
							{t("cancel", { ns: "common" })}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								for (const row of selectedRows) {
									if (row.status !== "disposed") {
										disposeAsset.mutate({
											id: row.id,
											reason: disposeReason,
										});
									}
								}
								setDisposeDialogOpen(false);
								setDisposeReason("");
							}}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{t("asset_dispose")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Create Asset Dialog
// ---------------------------------------------------------------------------

function CreateAssetDialog({
	onSubmit,
	isPending,
}: {
	onSubmit: (values: {
		name: string;
		assetNumber: string;
		acquisitionCost: string;
		depreciationMethod: string;
		usefulLifeMonths: number;
	}) => void;
	isPending: boolean;
}) {
	const { t } = useTranslation("accounting");
	const [name, setName] = useState("");
	const [assetNumber, setAssetNumber] = useState("");
	const [acquisitionCost, setAcquisitionCost] = useState("");
	const [depreciationMethod, setDepreciationMethod] = useState("straight_line");
	const [usefulLifeMonths, setUsefulLifeMonths] = useState("60");

	function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		onSubmit({
			name,
			assetNumber,
			acquisitionCost,
			depreciationMethod,
			usefulLifeMonths: Number.parseInt(usefulLifeMonths, 10) || 60,
		});
	}

	return (
		<DialogContent>
			<DialogHeader>
				<DialogTitle>{t("asset_create")}</DialogTitle>
			</DialogHeader>
			<form onSubmit={handleSubmit} className="space-y-4">
				<div className="space-y-2">
					<Label>{t("account_name")}</Label>
					<Input
						value={name}
						onChange={(e) => setName(e.target.value)}
						required
					/>
				</div>
				<div className="space-y-2">
					<Label>{t("asset_number")}</Label>
					<Input
						value={assetNumber}
						onChange={(e) => setAssetNumber(e.target.value)}
						required
					/>
				</div>
				<div className="space-y-2">
					<Label>{t("acquisition_cost")}</Label>
					<Input
						type="number"
						step="0.01"
						value={acquisitionCost}
						onChange={(e) => setAcquisitionCost(e.target.value)}
						required
					/>
				</div>
				<div className="space-y-2">
					<Label>{t("depreciation_method")}</Label>
					<Select
						value={depreciationMethod}
						onValueChange={setDepreciationMethod}
					>
						<SelectTrigger>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="straight_line">
								{t("depreciation_straight_line")}
							</SelectItem>
							<SelectItem value="declining_balance">
								{t("depreciation_declining_balance")}
							</SelectItem>
							<SelectItem value="units_of_production">
								{t("depreciation_units_of_production")}
							</SelectItem>
						</SelectContent>
					</Select>
				</div>
				<div className="space-y-2">
					<Label>{t("useful_life_months")}</Label>
					<Input
						type="number"
						value={usefulLifeMonths}
						onChange={(e) => setUsefulLifeMonths(e.target.value)}
						required
					/>
				</div>
				<DialogFooter>
					<Button type="submit" disabled={isPending}>
						{isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
						{t("asset_create")}
					</Button>
				</DialogFooter>
			</form>
		</DialogContent>
	);
}
